import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { EnelXGui } from 'gui-com-lib';
import { getOneTimePassword, getResetPassword } from '../../api';
import {
  StyledForm, Header, StyledButton, FlexBox, Text,
} from '../../main.styled';
import { CustomModal } from '../CustomModal';
import { validateRegister } from '../../utils/formValidation';
import { PasswordValidation } from '../common/PasswordValidation';
import { toastError } from '../common/ToastError';
import { getFromStorage } from '../../utils/storage';
import { getLanguage, getCountry } from '../../utils/common';
import appStoreImg from '../../assets/images/App_store_Outline.svg';
import googlePlayImg from '../../assets/images/Google_Play_Outline.svg';

const {
  SingleInput, Button, Checkbox,
} = EnelXGui.EnelXGuiAtoms;

const { Loader } = EnelXGui.EnelXGuiComponents;

const ResetPassword = () => {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(true);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errorModalText, setErrorModalText] = useState('');
  const [errorModalTitle, setErrorModalTitle] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [email, setEmail] = useState(JSON.parse(sessionStorage.getItem('email')) || '');
  const [otpResponse, setOtpResponse] = useState();
  const [passwordResetResponse, setPasswordResetResponse] = useState();
  const [newPassword, setNewPassword] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState('');
  const [otp, setOtp] = useState();
  const [session, setSession] = useState(getFromStorage('userId'));
  const params = new URLSearchParams(document.location.search);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const userRole = params.get('userRole');
  const country = params.get('country');
  const emailFromParam = params.get('email');

  // TODO: implement another api if needed since geolocation-db.com is down
  // const getIpData = async () => {
  //   try {
  //     const res = await axios.get('https://geolocation-db.com/json/');
  //     setIp(res?.data?.IPv4 || null);
  //     setCountryCode(res?.data?.country_code);
  //   } catch (error) {
  //     setIp(null);
  //   }
  // };

  let filters = {
    country: country || countryCode || getCountry(),
    language: params.get('language') || getLanguage(),
    sessionId: session,
    touchpoint: 'test',
    processType: 'RESET_PASSWORD',
  };

  if (userRole) {
    filters = { ...filters, userRole };
  }

  const body = { ...filters, username: emailFromParam || email };

  const headers = {};

  const getOtp = async () => {
    if (!emailFromParam && !email) {
      return toastError(t('Error_Empty_Field'));
    }

    try {
      setIsFetching(true);
      const response = (country || countryCode) && await getOneTimePassword(body, headers);
      if (response.data.code === '200') {
        setIsFetching(false);
        setModalOpen(false);
        setResetPasswordModalOpen(true);
        setOtpResponse(response);
        sessionStorage.removeItem('email');
        setTermsAccepted(false);
      }
    } catch (err) {
      setIsFetching(false);
      setModalOpen(false);
      setErrorModal(true);
      if (err?.response?.data?.message === 'Account not exists') {
        setErrorModalTitle(t('Error_Title_Wrong_Email'));
        setErrorModalText(t('Error_Message_Wrong_Email'));
      } else {
        setErrorModalText(t('Reset_Pw_Error_Message'));
        setErrorModalTitle(t('Reset_Pw_Error_Title'));
      }
      sessionStorage.removeItem('email');
      setTermsAccepted(false);
      return toastError(err?.response?.data?.message || err.message);
    }

    return otpResponse;
  };

  const passwordResetBody = {
    ...body, verificationId: otpResponse?.data.data.verificationId, newPassword, code: otp,
  };

  const resetPassword = async () => {
    if (!otp || !newPassword || !verifiedPassword) {
      return toastError(t('Error_Empty_Fields'));
    }

    if (!validateRegister(newPassword) || !validateRegister(verifiedPassword)) {
      return toastError(t('Toast_Pass_Incorrect'));
    }

    if (newPassword !== verifiedPassword) {
      return toastError(t('Toast_Pass_Mismatch'));
    }

    if (validateRegister(newPassword) && validateRegister(verifiedPassword)) {
      try {
        setIsFetching(true);
        const response = await getResetPassword(passwordResetBody, headers);
        if (response.data.code === '200') {
          setIsFetching(false);
          setPasswordResetResponse(response);
          setResetPasswordModalOpen(false);
        }
      } catch (err) {
        setIsFetching(false);
        setResetPasswordModalOpen(false);
        setErrorModal(true);
        return toastError(err.message);
      }
    }

    return passwordResetResponse;
  };

  const onClose = () => {
    setModalOpen(true);
    setResetPasswordModalOpen(false);
  };

  return (
    <>
      {
        isFetching && <Loader isSpinning />
      }
      { resetPasswordModalOpen
        && (
          <CustomModal
            width="432px"
            height="auto"
            position="relative"
            render={() => (
              <>
                <Header>{t('OTP_Code_Title')}</Header>
                <p>
                  {t('OTP_Code_Text')}
                </p>
                <StyledForm>
                  <SingleInput
                    width="296px"
                    type="text"
                    id="opt"
                    placeholder="OTP"
                    onChange={(e) => setOtp(e)}
                  />
                  <SingleInput
                    width="296px"
                    type="Password"
                    placeholder={t('New_Password')}
                    id="password"
                    onChange={(e) => setNewPassword(e)}
                  />
                  <SingleInput
                    width="296px"
                    type="Password"
                    placeholder={t('Confirm_Password')}
                    id="password"
                    onChange={(e) => setVerifiedPassword(e)}
                  />
                  <PasswordValidation password={newPassword} verifiedPassword={verifiedPassword} />
                  <StyledButton>
                    <Button
                      btnText={t('Confirm_Button')}
                      width="150px"
                      btnType="primary"
                      onClick={resetPassword}
                    />
                  </StyledButton>
                </StyledForm>
              </>
            )}
            closeModal={() => onClose()}
            closeIcon
          />
        )}
      {
        (passwordResetResponse && passwordResetResponse.data.code === '200')
          && (
            <CustomModal
              width="432px"
              height="auto"
              position="relative"
              render={() => (
                <>
                  <Header>{t('Password_Changed_Title')}</Header>
                  <p>
                    {t('Password_Changed_Text_USA')}
                  </p>
                  <FlexBox>
                    <a href="https://apps.apple.com/us/app/juicapass/id1377291789">
                      <img src={appStoreImg} alt="Download from App store" />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.enel.mobile.recharge2&hl=en_US">
                      <img src={googlePlayImg} alt="Download from Google play" />
                    </a>
                  </FlexBox>
                </>
              )}
            />
          )
      }
      { errorModal && (
        <CustomModal
          width="432px"
          position="relative"
          render={() => (
            <>
              <Header>{errorModalTitle}</Header>
              <p>
                {errorModalText}
              </p>
              <StyledButton>
                <Button
                  btnText={t('Try_Again_Button')}
                  width="200px"
                  btnType="primary"
                  onClick={() => {
                    setModalOpen(true);
                    setErrorModal(false);
                    setEmail('');
                  }}
                />
              </StyledButton>
            </>
          )}
        />
      )}
      {
        modalOpen && (
          <CustomModal
            width="432px"
            height="auto"
            position="relative"
            render={() => (
              <>
                <Header>{t('Set_Password_Title')}</Header>
                <p>
                  {t('Set_Password_Text')}
                </p>
                <StyledForm>
                  <SingleInput
                    width="100%"
                    type="email"
                    value={emailFromParam || email}
                    externalValue={email}
                    placeholder={emailFromParam || email || t('Email_placeholder')}
                    disabled={emailFromParam}
                    onChange={(e) => {
                      setEmail(e);
                      sessionStorage.setItem('email', JSON.stringify(e));
                    }}
                  />
                  <FlexBox>
                    <Checkbox
                      checked={termsAccepted}
                      handleOnChange={() => (termsAccepted ? setTermsAccepted(false) : setTermsAccepted(true))}
                    />
                    <Text style={{
                      whiteSpace: 'pre',
                    }}
                    >
                      {t('Terms_And_Privacy_Text')}
                      <a
                        href="https://www.company.enelxway.com/en/app/n-a/terms-and-conditions"
                        style={{
                          color: '#990099',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                        }}
                      >
                        {t('Terms_Text')}

                      </a>
                      <span>{t('And_Text')}</span>
                      <a
                        href="https://www.company.enelxway.com/en/app/n-a/privacy-policy"
                        style={{
                          color: '#990099',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                        }}
                      >
                        {t('Privacy_Text')}

                      </a>
                      <span>{t('Service_Text')}</span>
                    </Text>
                  </FlexBox>
                  <StyledButton>
                    <Button
                      btnText={t('Create_Password_Button')}
                      width="200px"
                      btnType="primary"
                      onClick={getOtp}
                      disabled={!termsAccepted}
                    />
                  </StyledButton>
                </StyledForm>
              </>
            )}
          />
        )
      }
    </>
  );
};

export { ResetPassword };
